import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ToolPage, { inputCls, labelCls, primaryBtn, secondaryBtn } from "@/components/ToolPage";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionLabel } from "@/components/Eyebrow";

const SCHOOLS = ["UF", "UCF", "FSU", "USF", "FIU", "Harvard", "Other"];
const STATUSES = { not_started: "Not started", in_progress: "In progress", completed: "Completed" };

export default function Eligibility() {
  const { user } = useAuth();
  const [stage, setStage] = useState("form");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    current_college: "Valencia College", credits_completed: 18, aa_status: "in_progress",
    current_major: "AA — General Education", intended_major: "Business Administration",
    current_gpa: 3.6, target_school: "UF", target_school_other: "",
  });
  const [email, setEmail] = useState(""); const [name, setName] = useState("");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const next = (e) => { e.preventDefault(); setStage(user ? "running" : "gate"); if (user) run(); };

  const run = async (e) => {
    if (e) e.preventDefault();
    if (!user && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { toast.error("Please enter a valid email."); return; }
    setLoading(true);
    try {
      const payload = {
        ...form,
        credits_completed: parseInt(form.credits_completed, 10) || 0,
        current_gpa: parseFloat(form.current_gpa) || 0,
        email: email || undefined, name: name || undefined,
      };
      const res = await api.post("/tools/eligibility", payload);
      setResult(res.data); setStage("result");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Tool run failed.";
      toast.error(msg);
      if (err?.response?.status === 429) setStage("form");
    } finally { setLoading(false); }
  };

  return (
    <ToolPage
      numeral="I"
      name="Transfer Eligibility Checker"
      blurb="A read on eligibility, competitiveness, and pathway classification for the school you have in mind."
      seoTitle="Free Transfer Eligibility Checker — UF, UCF, FSU, Harvard | Beyond Valencia"
      seoDescription="Instantly check your transfer eligibility to UF, UCF, FSU, and other Florida universities. AI-powered admissions read for community college students."
      seoCanonical="/tools/eligibility"
      seoKeywords="transfer eligibility checker, UF transfer eligibility, UCF DirectConnect eligibility, Valencia transfer, Florida transfer eligibility"
    >
      {stage === "form" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-3xl">
          <SectionLabel index="A" label="Intake" />
          <form onSubmit={next} className="mt-7 space-y-5" data-testid="eligibility-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label className={labelCls}>Current college</Label><Input data-testid="el-current-college" className={inputCls} value={form.current_college} onChange={set("current_college")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Credits completed</Label><Input type="number" data-testid="el-credits" className={inputCls} value={form.credits_completed} onChange={set("credits_completed")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>AA status</Label>
                <select className={`${inputCls} w-full h-9 px-3 text-sm`} value={form.aa_status} onChange={set("aa_status")}>{Object.entries(STATUSES).map(([k, v]) => (<option key={k} value={k}>{v}</option>))}</select>
              </div>
              <div className="space-y-2"><Label className={labelCls}>Current GPA</Label><Input data-testid="el-gpa" className={inputCls} value={form.current_gpa} onChange={set("current_gpa")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Current major</Label><Input className={inputCls} value={form.current_major} onChange={set("current_major")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Intended major</Label><Input data-testid="el-intended-major" className={inputCls} value={form.intended_major} onChange={set("intended_major")} required /></div>
              <div className="space-y-2"><Label className={labelCls}>Target school</Label>
                <select className={`${inputCls} w-full h-9 px-3 text-sm`} data-testid="el-target-school" value={form.target_school} onChange={set("target_school")}>{SCHOOLS.map((s) => (<option key={s} value={s}>{s}</option>))}</select>
              </div>
              {form.target_school === "Other" && (<div className="space-y-2"><Label className={labelCls}>Other school name</Label><Input className={inputCls} value={form.target_school_other} onChange={set("target_school_other")} /></div>)}
            </div>
            <button type="submit" className={primaryBtn} data-testid="eligibility-next-btn">Continue</button>
          </form>
        </div>
      )}

      {stage === "gate" && (
        <div className="border border-stone/40 bg-bone p-6 sm:p-10 max-w-2xl" data-testid="eligibility-gate">
          <SectionLabel index="B" label="One step before we run it" />
          <h2 className="font-serif font-medium text-2xl sm:text-3xl text-ink mt-4 tracking-tight">Where should we send the read?</h2>
          <p className="text-ink-soft text-sm mt-3 leading-relaxed max-w-md">We'll email you the result and keep you on the (low-volume) field-notes list. Unsubscribe anytime.</p>
          <form onSubmit={run} className="mt-7 space-y-5">
            <div className="space-y-2"><Label className={labelCls}>First name</Label><Input data-testid="el-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-2"><Label className={labelCls}>Email</Label><Input type="email" data-testid="el-email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div className="flex gap-3"><button type="submit" disabled={loading} className={primaryBtn} data-testid="eligibility-submit-btn">{loading ? "Analyzing…" : "Run my eligibility check"}</button><button type="button" onClick={() => setStage("form")} className={secondaryBtn}>Back</button></div>
          </form>
        </div>
      )}

      {(stage === "running" || loading) && stage !== "result" && (<div className="text-ink-soft" data-testid="eligibility-running">Analyzing your profile against the Florida transfer system… this can take 10-25 seconds.</div>)}

      {stage === "result" && result && (
        <div className="space-y-0 border-t border-l border-stone/40" data-testid="eligibility-result">
          <div className="border-r border-b border-stone/40 bg-bone-dim p-7">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Applicant type</div>
            <p className="font-serif font-medium text-2xl text-ink mt-3 tracking-tight" data-testid="el-result-applicant-type">{result.applicant_type}</p>
          </div>
          <div className="grid sm:grid-cols-2 border-r border-b border-stone/40">
            <div className="p-7 border-r border-stone/40 bg-bone"><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Eligibility</div><p className="font-serif font-medium text-xl text-ink mt-3 tracking-tight">{result.eligibility_status}</p><p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-stone">Pathway: {result.pathway_classification}</p></div>
            <div className="p-7 bg-bone"><div className="text-[10px] uppercase tracking-[0.22em] text-brass">Competitiveness</div><p className="font-serif font-medium text-xl text-ink mt-3 tracking-tight">{result.competitiveness}</p></div>
          </div>
          <div className="border-r border-b border-stone/40 bg-bone p-7">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Risk factors</div>
            <ul className="mt-4 space-y-2.5">{(result.risk_factors || []).map((r, i) => (<li key={i} className="flex gap-3 text-sm text-ink"><span className="text-brass mt-2 h-px w-3 bg-brass flex-shrink-0" /><span>{r}</span></li>))}</ul>
          </div>
          <div className="border-r border-b border-stone/40 bg-bone p-7" data-testid="el-result-next-steps">
            <SectionLabel index="04" label="Next steps" />
            <ol className="mt-6 space-y-6">{(result.next_steps || []).map((s, i) => (
              <li key={i} className="border-l-2 border-brass pl-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brass">Step {String(i + 1).padStart(2, "0")} · {s.deadline_window} · {s.responsible_party}</div>
                <p className="font-serif text-lg text-ink mt-2 tracking-tight">{s.action}</p>
                {s.required_documents?.length > 0 && <p className="text-xs text-stone mt-2">Docs: {s.required_documents.join(", ")}</p>}
                {s.risks?.length > 0 && <p className="text-xs text-stone mt-1">Risks: {s.risks.join("; ")}</p>}
              </li>
            ))}</ol>
          </div>
          <div className="border-r border-b border-stone/40 bg-ivy text-bone p-7">
            <div className="text-[10px] uppercase tracking-[0.22em] text-brass-soft">Backup pathways</div>
            <ul className="mt-4 space-y-2">{(result.backup_pathways || []).map((b, i) => (<li key={i} className="text-sm text-bone-dim">— {b}</li>))}</ul>
          </div>
          <p className="text-[11px] text-stone mt-6 italic">{result.disclaimer}</p>
        </div>
      )}
    </ToolPage>
  );
}
